import type { ComicSource, SourceId } from './types';

/**
 * Auto-discovery registry for source modules.
 *
 * Every folder under `src/sources/` that:
 *   1. has a file named `index.ts`
 *   2. does NOT start with `_` (so `_template` is excluded)
 *
 * will be auto-loaded. Each module must export a `ComicSource` either as
 * its default export or as a named export called `source`.
 *
 * Adding a new source = adding a new folder with NO central edit. This is
 * load-bearing for our parallel-worker workflow: four agents fan out and
 * touch only `src/sources/<their-id>/`, never this file.
 */

type SourceModule =
  | { default: ComicSource }
  | { source: ComicSource };

const modules = import.meta.glob<SourceModule>('./*/index.ts', { eager: true });

function pickSource(mod: SourceModule): ComicSource | undefined {
  if ('default' in mod && mod.default && typeof mod.default === 'object') return mod.default;
  if ('source' in mod && mod.source && typeof mod.source === 'object') return mod.source;
  return undefined;
}

function isExcludedPath(modPath: string): boolean {
  // glob is relative to this file, e.g. `./_template/index.ts` or `./hitomi/index.ts`
  const dir = modPath.split('/').at(1);
  if (!dir) return true;
  return dir.startsWith('_') || dir.startsWith('.');
}

const sourcesById = new Map<SourceId, ComicSource>();
const loadErrors: Array<{ path: string; reason: string }> = [];

for (const [modPath, mod] of Object.entries(modules)) {
  if (isExcludedPath(modPath)) continue;
  const src = pickSource(mod);
  if (!src) {
    loadErrors.push({
      path: modPath,
      reason: 'module did not export `default` or `source` of type ComicSource',
    });
    continue;
  }
  if (sourcesById.has(src.id)) {
    loadErrors.push({
      path: modPath,
      reason: `duplicate source id "${src.id}"`,
    });
    continue;
  }
  sourcesById.set(src.id, src);
}

/** All registered sources, in stable id-sorted order for deterministic UI. */
export function getAllSources(): ComicSource[] {
  return [...sourcesById.values()].sort((a, b) => a.id.localeCompare(b.id));
}

/** Look up a source by id, or `undefined` if not registered. */
export function getSource(id: string): ComicSource | undefined {
  return sourcesById.get(id as SourceId);
}

/** Same as {@link getSource} but throws — convenient for route handlers. */
export function requireSource(id: string): ComicSource {
  const src = getSource(id);
  if (!src) throw new Error(`Unknown source: ${id}`);
  return src;
}

/** Diagnostic info, useful for a future "registered sources" debug panel. */
export function getRegistryDiagnostics(): {
  count: number;
  ids: SourceId[];
  errors: Array<{ path: string; reason: string }>;
} {
  return {
    count: sourcesById.size,
    ids: [...sourcesById.keys()],
    errors: loadErrors,
  };
}
